import React, { useState, useEffect, Fragment } from "react";
import {
  Box,
  Button,
  Flex,
  Text,
  useColorModeValue,
  Input,
  InputGroup,
  InputLeftElement,
  useToast,
  VStack,
  HStack,
  Spinner,
  Badge,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  FormErrorMessage,
  useDisclosure,
  IconButton,
  Tooltip,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Heading,
  Divider,
  Wrap,
  WrapItem,
  Tag,
  TagLabel,
  TagCloseButton,
} from "@chakra-ui/react";
import {
  SearchIcon,
  AddIcon,
  EditIcon,
  DeleteIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@chakra-ui/icons";
import { rolesService } from "api";
import httpClient from "api/httpClient.js";
import Card from "components/Card/Card.js";
import CardHeader from "components/Card/CardHeader.js";
import CardBody from "components/Card/CardBody.js";

const RolesPermissions = () => {
  const [roles, setRoles] = useState([]);
  const [filteredRoles, setFilteredRoles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedRole, setSelectedRole] = useState(null);
  const [editingRole, setEditingRole] = useState(null);
  const [actionLoading, setActionLoading] = useState({
    create: false,
    update: false,
    delete: false,
  });
  const [validationErrors, setValidationErrors] = useState({});
  const [allPermissions, setAllPermissions] = useState([]);
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [permissionSearchTerm, setPermissionSearchTerm] = useState("");
  const [roleName, setRoleName] = useState("");

  // Modal states
  const { isOpen: isModalOpen, onOpen: onModalOpen, onClose: onModalClose } = useDisclosure();
  const { isOpen: isPermissionsOpen, onOpen: onPermissionsOpen, onClose: onPermissionsClose } = useDisclosure();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();

  const toast = useToast();
  const textColor = useColorModeValue("gray.700", "white");
  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.600");

  // Fetch roles and permissions
  const fetchRoles = async () => {
    setIsLoading(true);
    try {
      const response = await rolesService.getRoles();
      console.log('API Response:', response); // Debug log
      
      if (response.success || response.status === 1) {
        const rolesData = response.data.data || [];
        console.log('Roles Data:', rolesData); // Debug log
        
                 // Ensure rolesData is an array
         if (Array.isArray(rolesData)) {
           setRoles(rolesData);
           setFilteredRoles(rolesData);
           
           // Extract all unique permissions from roles data
           const allPermissions = extractAllPermissions(rolesData);
           setAllPermissions(allPermissions);
         } else {
          console.error('Roles data is not an array:', rolesData);
          setRoles([]);
          setFilteredRoles([]);
          toast({
            title: "خطأ",
            description: "بيانات الأدوار غير صحيحة",
            status: "error",
            duration: 5000,
            isClosable: true,
          });
        }
      } else {
        console.error('API response error:', response);
        setRoles([]);
        setFilteredRoles([]);
        toast({
          title: "خطأ",
          description: response.message || "حدث خطأ أثناء جلب الأدوار والصلاحيات",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error("Error fetching roles:", error);
      
      // Check if it's an authentication error
      if (error.message && error.message.includes('Unauthenticated')) {
        toast({
          title: "انتهت صلاحية الجلسة",
          description: "سيتم توجيهك إلى صفحة تسجيل الدخول",
          status: "warning",
          duration: 3000,
          isClosable: true,
        });
        // The httpClient will handle the redirect
        return;
      }
      
      setRoles([]);
      setFilteredRoles([]);
      toast({
        title: "خطأ",
        description: "حدث خطأ أثناء جلب الأدوار والصلاحيات",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Extract all unique permissions from roles data
  const extractAllPermissions = (rolesData) => {
    const allPermissionsSet = new Set();
    const permissionsMap = new Map();
    
    rolesData.forEach(role => {
      if (role.permissions && Array.isArray(role.permissions)) {
        role.permissions.forEach(permission => {
          // Use permission name as key to avoid duplicates
          const key = permission.name;
          if (!permissionsMap.has(key)) {
            permissionsMap.set(key, permission);
            allPermissionsSet.add(permission);
          }
        });
      }
    });
    
    // Convert to array and sort by ID for consistent ordering
    const allPermissions = Array.from(allPermissionsSet).sort((a, b) => a.id - b.id);
    console.log('Extracted permissions from roles:', allPermissions);
    return allPermissions;
  };

  // Fetch all available permissions
  const fetchAllPermissions = async () => {
    try {
      // Since the permissions API endpoint doesn't exist yet, we'll extract permissions from roles data
      // This will be replaced when the actual API endpoint is available
      console.log('Permissions API endpoint not available, extracting from roles data');
      
      // We'll call this after roles are loaded
      // For now, set empty array and wait for roles to load
      setAllPermissions([]);
    } catch (error) {
      console.error("Error in fetchAllPermissions:", error);
      toast({
        title: "خطأ",
        description: "حدث خطأ أثناء جلب الصلاحيات",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  useEffect(() => {
    fetchRoles();
    // fetchAllPermissions(); // Removed - permissions are now extracted from roles data
  }, []);

  // Filter roles based on search term
  useEffect(() => {
    console.log('Filter effect - roles:', roles, 'filteredRoles:', filteredRoles); // Debug log
    
    if (!Array.isArray(roles)) {
      console.log('Roles is not an array, setting filteredRoles to empty array'); // Debug log
      setFilteredRoles([]);
      return;
    }
    
    if (searchTerm.trim() === "") {
      setFilteredRoles(roles);
    } else {
      const filtered = roles.filter(role =>
        role.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredRoles(filtered);
    }
  }, [searchTerm, roles]);

  // Handle search
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  // Handle view permissions
  const handleViewPermissions = (role) => {
    setSelectedRole(role);
    onPermissionsOpen();
  };

  // Handle edit role
  const handleEditRole = (role) => {
    setEditingRole(role);
    setRoleName(role.name || "");
    setSelectedPermissions(role.permissions || []);
    onModalOpen();
  };

  // Handle delete role
  const handleDeleteRole = (role) => {
    setSelectedRole(role);
    onDeleteOpen();
  };

  // Handle modal close
  const handleModalClose = () => {
    setEditingRole(null);
    setRoleName("");
    setSelectedPermissions([]);
    setPermissionSearchTerm("");
    setValidationErrors({});
    onModalClose();
  };

  // Handle permissions modal close
  const handlePermissionsClose = () => {
    setSelectedRole(null);
    onPermissionsClose();
  };

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (!selectedRole) return;

    setActionLoading(prev => ({ ...prev, delete: true }));
    try {
      // TODO: Implement delete API call
      toast({
        title: "تم الحذف",
        description: `تم حذف الدور "${selectedRole.name}" بنجاح`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      
      // Refresh roles list
      await fetchRoles();
      onDeleteClose();
    } catch (error) {
      toast({
        title: "خطأ",
        description: "حدث خطأ أثناء حذف الدور",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setActionLoading(prev => ({ ...prev, delete: false }));
    }
  };

  // Handle create new role
  const handleCreateRole = () => {
    setEditingRole({});
    setRoleName("");
    setSelectedPermissions([]);
    onModalOpen();
  };

  // Handle form submission
  const handleSubmit = async () => {
    // Validate role name
    if (!roleName.trim()) {
      setValidationErrors({ name: "اسم الدور مطلوب" });
      return;
    }

    if (editingRole?.id) {
      // Update existing role
      setActionLoading(prev => ({ ...prev, update: true }));
      try {
        // Prepare form data for API
        const formData = new FormData();
        formData.append('name', roleName);
        
        // Add permissions as array
        selectedPermissions.forEach(permission => {
          formData.append('permissions[]', permission.name);
        });

        // TODO: Implement update API call
        console.log('Updating role with permissions:', selectedPermissions);
        console.log('Form data:', formData);
        
        toast({
          title: "تم التحديث",
          description: "تم تحديث الدور بنجاح",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        
        await fetchRoles();
        handleModalClose();
      } catch (error) {
        toast({
          title: "خطأ",
          description: "حدث خطأ أثناء تحديث الدور",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setActionLoading(prev => ({ ...prev, update: false }));
      }
    } else {
      // Create new role
      setActionLoading(prev => ({ ...prev, create: true }));
      try {
        // Prepare form data for API
        const formData = new FormData();
        formData.append('name', roleName);
        
        // Add permissions as array
        selectedPermissions.forEach(permission => {
          formData.append('permissions[]', permission.name);
        });

        console.log('Creating role with form data:', {
          name: roleName,
          permissions: selectedPermissions.map(p => p.name)
        });

        // Make API call to create role using httpClient
        const response = await httpClient.post('/api/admin/roles-permissions', formData);
        
        if (response.success && (response.data.status === 1 || response.data.success)) {
          toast({
            title: "تم الإنشاء",
            description: "تم إنشاء الدور بنجاح",
            status: "success",
            duration: 3000,
            isClosable: true,
          });
          
          // Refresh roles list
          await fetchRoles();
          handleModalClose();
        } else {
          throw new Error(response.data.message || 'فشل في إنشاء الدور');
        }
      } catch (error) {
        console.error('Error creating role:', error);
        
        // Check if it's an authentication error
        if (error.message && error.message.includes('Unauthenticated')) {
          toast({
            title: "انتهت صلاحية الجلسة",
            description: "سيتم توجيهك إلى صفحة تسجيل الدخول",
            status: "warning",
            duration: 3000,
            isClosable: true,
          });
          // The httpClient will handle the redirect
          return;
        }
        
        toast({
          title: "خطأ",
          description: error.message || "حدث خطأ أثناء إنشاء الدور",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setActionLoading(prev => ({ ...prev, create: false }));
      }
    }
  };

  // Handle permission selection
  const handlePermissionToggle = (permission) => {
    setSelectedPermissions(prev => {
      const isSelected = prev.some(p => p.id === permission.id);
      if (isSelected) {
        return prev.filter(p => p.id !== permission.id);
      } else {
        return [...prev, permission];
      }
    });
  };

  // Check if permission is selected
  const isPermissionSelected = (permission) => {
    return selectedPermissions.some(p => p.id === permission.id);
  };

  // Filter permissions based on search term
  const filteredPermissions = allPermissions.filter(permission =>
    permission.name.toLowerCase().includes(permissionSearchTerm.toLowerCase())
  );

  // Handle select all permissions
  const handleSelectAll = () => {
    setSelectedPermissions([...allPermissions]);
  };

  // Handle clear all permissions
  const handleClearAll = () => {
    setSelectedPermissions([]);
  };

  // Check if all permissions are selected
  const isAllSelected = selectedPermissions.length === allPermissions.length && allPermissions.length > 0;

  // Debug log for render
  console.log('Render - roles:', roles, 'filteredRoles:', filteredRoles, 'isLoading:', isLoading);
  
  return (
    <Box pt={{ base: "130px", md: "80px", xl: "80px" }}>
      <Flex direction="column" w="100%">
        <Card
          maxW="100%"
          w="100%"
          bg={bgColor}
          border="1px solid"
          borderColor={borderColor}
          borderRadius="15px"
          boxShadow="0 4px 20px rgba(0, 0, 0, 0.1)"
        >
          <CardHeader>
            <Flex justify="space-between" align="center" flexWrap="wrap">
              <Box>
                <Heading size="lg" color={textColor} mb={2}>
                  الأدوار والصلاحيات
                </Heading>
              </Box>
              <Button
                leftIcon={<AddIcon />}
                colorScheme="blue"
                size="lg"
                borderRadius="8px"
                onClick={handleCreateRole}
              >
                إضافة دور جديد
              </Button>
            </Flex>
          </CardHeader>

          <CardBody>
            {/* Search and Filters */}
            <Flex mb={6}>
              <InputGroup maxW="400px">
                <InputLeftElement pointerEvents="none">
                  <SearchIcon color="gray.400" />
                </InputLeftElement>
                <Input
                  placeholder="البحث في الأدوار..."
                  value={searchTerm}
                  onChange={handleSearch}
                  borderRadius="8px"
                  border="1px solid"
                  borderColor={borderColor}
                />
              </InputGroup>
            </Flex>

            {/* Roles Table */}
            {isLoading ? (
              <Flex justify="center" align="center" py={20}>
                <VStack spacing={4}>
                  <Spinner size="xl" color="blue.500" />
                  <Text color="gray.500">جاري تحميل الأدوار والصلاحيات...</Text>
                </VStack>
              </Flex>
            ) : (
              <TableContainer>
                <Table variant="simple">
                  <Thead>
                    <Tr>
                      <Th color={textColor} fontSize="md" fontWeight="bold">اسم الدور</Th>
                      <Th color={textColor} fontSize="md" fontWeight="bold">عدد المستخدمين</Th>
                      <Th color={textColor} fontSize="md" fontWeight="bold">عدد الصلاحيات</Th>
                      <Th color={textColor} fontSize="md" fontWeight="bold">تاريخ الإنشاء</Th>
                      <Th color={textColor} fontSize="md" fontWeight="bold" textAlign="center">الإجراءات</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {Array.isArray(filteredRoles) && filteredRoles.map((role) => (
                      <Tr key={role.id}>
                        <Td>
                          <Badge
                            px={3}
                            py={1}
                            borderRadius="full"
                            fontSize="sm"
                            fontWeight="bold"
                            colorScheme="blue"
                            variant="subtle"
                          >
                            {role.name}
                          </Badge>
                        </Td>
                        <Td>
                          <Text fontWeight="medium" color={textColor}>
                            {role.users_count}
                          </Text>
                        </Td>
                        <Td>
                          <Text fontWeight="medium" color={textColor}>
                            {role.permissions?.length || 0}
                          </Text>
                        </Td>
                        <Td>
                          <Text color="gray.600" fontSize="sm">
                            {new Date(role.created_at).toLocaleDateString('en-US')}
                          </Text>
                        </Td>
                        <Td>
                          <Flex justify="center">
                            <Tooltip label="عرض الصلاحيات" placement="top">
                              <IconButton
                                icon={<SearchIcon />}
                                colorScheme="blue"
                                variant="ghost"
                                size="sm"
                                onClick={() => handleViewPermissions(role)}
                                aria-label="عرض الصلاحيات"
                              />
                            </Tooltip>
                            <Tooltip label="تعديل الدور" placement="top">
                              <IconButton
                                icon={<EditIcon />}
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEditRole(role)}
                                aria-label="تعديل الدور"
                              />
                            </Tooltip>
                            <Tooltip label="حذف الدور" placement="top">
                              <IconButton
                                icon={<DeleteIcon />}
                                colorScheme="red"
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteRole(role)}
                                aria-label="حذف الدور"
                                isDisabled={role.users_count > 0}
                              />
                            </Tooltip>
                          </Flex>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </TableContainer>
            )}

            {!isLoading && Array.isArray(filteredRoles) && filteredRoles.length === 0 && (
              <Flex justify="center" align="center" py={20}>
                <VStack spacing={4}>
                  <Text color="gray.500" fontSize="lg">
                    {searchTerm ? "لا توجد نتائج للبحث" : "لا توجد أدوار متاحة"}
                  </Text>
                  {searchTerm && (
                    <Button
                      variant="outline"
                      onClick={() => setSearchTerm("")}
                    >
                      مسح البحث
                    </Button>
                  )}
                </VStack>
              </Flex>
            )}
          </CardBody>
        </Card>
      </Flex>

      {/* Permissions Modal */}
      <Modal isOpen={isPermissionsOpen} onClose={handlePermissionsClose} size="xl">
        <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
        <ModalContent mx="20px" maxW="800px" borderRadius="15px">
          <ModalHeader borderBottom="1px solid" borderColor={borderColor} pb="20px">
            صلاحيات دور: {selectedRole?.name}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody py="20px">
            {selectedRole?.permissions && selectedRole.permissions.length > 0 ? (
              <VStack spacing={4} align="stretch">
                <Text fontSize="sm" color="gray.600" mb={4}>
                  إجمالي الصلاحيات: {selectedRole.permissions.length}
                </Text>
                <Wrap spacing={2}>
                  {selectedRole.permissions.map((permission) => (
                    <WrapItem key={permission.id}>
                      <Tag
                        size="lg"
                        borderRadius="full"
                        variant="solid"
                        colorScheme="blue"
                      >
                        <TagLabel>{permission.name}</TagLabel>
                      </Tag>
                    </WrapItem>
                  ))}
                </Wrap>
              </VStack>
            ) : (
              <Text textAlign="center" color="gray.500" py={8}>
                لا توجد صلاحيات لهذا الدور
              </Text>
            )}
          </ModalBody>
          <ModalFooter borderTop="1px solid" borderColor={borderColor} pt="20px">
            <Button onClick={handlePermissionsClose} colorScheme="blue">
              إغلاق
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

             {/* Create/Edit Role Modal */}
       <Modal isOpen={isModalOpen} onClose={handleModalClose} size="lg">
         <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
         <ModalContent mx="20px" maxW="600px" borderRadius="15px" mt="100px">
           <ModalHeader borderBottom="1px solid" borderColor={borderColor} pb="20px">
             {editingRole?.id ? "تعديل الدور" : "إضافة دور جديد"}
           </ModalHeader>
          <ModalCloseButton />
          <ModalBody py="20px">
            <VStack spacing={4}>
                             <FormControl isInvalid={!!validationErrors.name}>
                 <FormLabel>اسم الدور</FormLabel>
                 <Input
                   placeholder="أدخل اسم الدور"
                   value={roleName}
                   onChange={(e) => setRoleName(e.target.value)}
                   borderRadius="8px"
                   border="1px solid"
                   borderColor={borderColor}
                 />
                 {validationErrors.name && (
                   <FormErrorMessage>{validationErrors.name}</FormErrorMessage>
                 )}
               </FormControl>
               
               <FormControl>
                 <FormLabel>الصلاحيات</FormLabel>
                 <Box maxH="300px" overflowY="auto" border="1px solid" borderColor={borderColor} borderRadius="8px" p={3}>
                   <Text fontSize="sm" color="gray.600" mb={3}>
                     اختر الصلاحيات المطلوبة (تم تحديد {selectedPermissions.length} صلاحية)
                   </Text>
                   
                   {/* Select All / Clear All Buttons */}
                   <Flex gap={2} mb={3}>
                     <Button
                       size="sm"
                       variant="outline"
                       colorScheme="blue"
                       onClick={handleSelectAll}
                       isDisabled={isAllSelected}
                     >
                       تحديد الكل
                     </Button>
                     <Button
                       size="sm"
                       variant="outline"
                       colorScheme="red"
                       onClick={handleClearAll}
                       isDisabled={selectedPermissions.length === 0}
                     >
                       إلغاء الكل
                     </Button>
                   </Flex>
                   
                   {/* Permission Search */}
                   <InputGroup mb={3}>
                     <InputLeftElement pointerEvents="none">
                       <SearchIcon color="gray.400" />
                     </InputLeftElement>
                     <Input
                       placeholder="البحث في الصلاحيات..."
                       value={permissionSearchTerm}
                       onChange={(e) => setPermissionSearchTerm(e.target.value)}
                       borderRadius="8px"
                       border="1px solid"
                       borderColor={borderColor}
                       size="sm"
                     />
                   </InputGroup>
                   
                   <Wrap spacing={2}>
                     {filteredPermissions.map((permission) => (
                       <WrapItem key={permission.id}>
                         <Tag
                           size="md"
                           borderRadius="full"
                           variant={isPermissionSelected(permission) ? "solid" : "outline"}
                           colorScheme={isPermissionSelected(permission) ? "blue" : "gray"}
                           cursor="pointer"
                           onClick={() => handlePermissionToggle(permission)}
                           _hover={{
                             bg: isPermissionSelected(permission) ? "blue.600" : "gray.100"
                           }}
                           borderWidth={isPermissionSelected(permission) ? "2px" : "1px"}
                           fontWeight={isPermissionSelected(permission) ? "bold" : "normal"}
                         >
                           <TagLabel>{permission.name}</TagLabel>
                         </Tag>
                       </WrapItem>
                     ))}
                   </Wrap>
                   
                   {filteredPermissions.length === 0 && permissionSearchTerm && (
                     <Text textAlign="center" color="gray.500" py={4}>
                       لا توجد نتائج للبحث
                     </Text>
                   )}
                   
                   {/* Show total count */}
                   <Text fontSize="xs" color="gray.500" mt={3} textAlign="center">
                     إجمالي الصلاحيات المتاحة: {allPermissions.length}
                   </Text>
                 </Box>
               </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter borderTop="1px solid" borderColor={borderColor} pt="20px">
            <Button variant="outline" onClick={handleModalClose} mr={3}>
              إلغاء
            </Button>
                         <Button
               colorScheme="blue"
               onClick={handleSubmit}
               isLoading={actionLoading.create || actionLoading.update}
               loadingText={editingRole?.id ? "جاري التحديث..." : "جاري الإنشاء..."}
             >
               {editingRole?.id ? "تحديث" : "إنشاء"}
             </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Confirmation Dialog */}
      <AlertDialog isOpen={isDeleteOpen} onClose={onDeleteClose} isCentered>
        <AlertDialogOverlay bg="blackAlpha.300" backdropFilter="blur(10px)">
          <AlertDialogContent
            mx="20px"
            maxW="500px"
            borderRadius="15px"
            boxShadow="0 20px 60px rgba(0, 0, 0, 0.3)"
            border="1px solid"
            borderColor={borderColor}
          >
            <AlertDialogHeader
              fontSize="xl"
              fontWeight="bold"
              textAlign="center"
              borderBottom="1px solid"
              borderColor={borderColor}
              pb="20px"
            >
              حذف الدور
            </AlertDialogHeader>
            <AlertDialogBody
              pt="20px"
              pb="20px"
              textAlign="center"
              fontSize="md"
              color="gray.600"
            >
              هل أنت متأكد من حذف الدور <strong>"{selectedRole?.name}"</strong>؟
              {selectedRole?.users_count > 0 && (
                <Text color="red.500" mt={2}>
                  تحذير: هذا الدور مرتبط بـ {selectedRole.users_count} مستخدم
                </Text>
              )}
            </AlertDialogBody>
            <AlertDialogFooter
              borderTop="1px solid"
              borderColor={borderColor}
              pt="20px"
            >
              <Button
                onClick={onDeleteClose}
                size="lg"
                minW="120px"
                borderRadius="8px"
                variant="outline"
                border="2px solid"
                borderColor="gray.300"
              >
                إلغاء
              </Button>
              <Button
                colorScheme="red"
                onClick={handleDeleteConfirm}
                size="lg"
                minW="120px"
                borderRadius="8px"
                isLoading={actionLoading.delete}
                loadingText="جاري الحذف..."
              >
                حذف
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default RolesPermissions;
